import { useEffect, useRef, useState, type JSX, type ChangeEvent } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ccaIconRGB, CCA_HOME_URL, fieldProps, stamp, StickyActionBar } from '@cca/ui';
import { AuthLayout } from '../auth/AuthLayout';
import { IdentityChooser, type IdentityChoice } from './IdentityChooser';
import { IS_CHILDRENS_WORKER } from './children-act';
import {
  OTHER_NAME_MAX_LENGTH,
  OTHER_NAMES_MAX_ROWS,
  OTHER_NAMES_MESSAGES,
  isBlankOtherName,
  otherNamesProblem,
} from './other-names';
import {
  PAPER_RECORD_CHANGED,
  PAPER_REVIEW_BODY,
  PAPER_REVIEW_HEADING,
  classifyPaperIdentityError,
  type CandidatesPayload,
} from './identity-review';

// ── Country list — NZ/Pacific/AU/UK first, then alphabetical ──────────────
const COUNTRIES = [
  'New Zealand',
  'Australia',
  'Samoa',
  'Tonga',
  'Fiji',
  'Cook Islands',
  'Niue',
  'Tokelau',
  'Papua New Guinea',
  'Solomon Islands',
  'Vanuatu',
  'United Kingdom',
  'United States',
  'Canada',
  'South Africa',
  'India',
  'Philippines',
  'China',
  'South Korea',
  'Japan',
  'Singapore',
  '──────────────',
  'Afghanistan',
  'Albania',
  'Algeria',
  'Andorra',
  'Angola',
  'Antigua and Barbuda',
  'Argentina',
  'Armenia',
  'Austria',
  'Azerbaijan',
  'Bahamas',
  'Bahrain',
  'Bangladesh',
  'Barbados',
  'Belarus',
  'Belgium',
  'Belize',
  'Benin',
  'Bhutan',
  'Bolivia',
  'Bosnia and Herzegovina',
  'Botswana',
  'Brazil',
  'Brunei',
  'Bulgaria',
  'Burkina Faso',
  'Burundi',
  'Cambodia',
  'Cameroon',
  'Cape Verde',
  'Central African Republic',
  'Chad',
  'Chile',
  'Colombia',
  'Comoros',
  'Congo',
  'Costa Rica',
  'Croatia',
  'Cuba',
  'Cyprus',
  'Czech Republic',
  'Denmark',
  'Djibouti',
  'Dominica',
  'Dominican Republic',
  'Ecuador',
  'Egypt',
  'El Salvador',
  'Equatorial Guinea',
  'Eritrea',
  'Estonia',
  'Eswatini',
  'Ethiopia',
  'France',
  'Gabon',
  'Gambia',
  'Georgia',
  'Germany',
  'Ghana',
  'Greece',
  'Grenada',
  'Guatemala',
  'Guinea',
  'Guinea-Bissau',
  'Guyana',
  'Haiti',
  'Honduras',
  'Hungary',
  'Iceland',
  'Indonesia',
  'Iran',
  'Iraq',
  'Ireland',
  'Israel',
  'Italy',
  'Ivory Coast',
  'Jamaica',
  'Jordan',
  'Kazakhstan',
  'Kenya',
  'Kiribati',
  'Kuwait',
  'Kyrgyzstan',
  'Laos',
  'Latvia',
  'Lebanon',
  'Lesotho',
  'Liberia',
  'Libya',
  'Liechtenstein',
  'Lithuania',
  'Luxembourg',
  'Madagascar',
  'Malawi',
  'Malaysia',
  'Maldives',
  'Mali',
  'Malta',
  'Marshall Islands',
  'Mauritania',
  'Mauritius',
  'Mexico',
  'Micronesia',
  'Moldova',
  'Monaco',
  'Mongolia',
  'Montenegro',
  'Morocco',
  'Mozambique',
  'Myanmar',
  'Namibia',
  'Nauru',
  'Nepal',
  'Netherlands',
  'Nicaragua',
  'Niger',
  'Nigeria',
  'North Korea',
  'North Macedonia',
  'Norway',
  'Oman',
  'Pakistan',
  'Palau',
  'Palestine',
  'Panama',
  'Paraguay',
  'Peru',
  'Poland',
  'Portugal',
  'Qatar',
  'Romania',
  'Russia',
  'Rwanda',
  'Saint Kitts and Nevis',
  'Saint Lucia',
  'Saint Vincent and the Grenadines',
  'San Marino',
  'Sao Tome and Principe',
  'Saudi Arabia',
  'Senegal',
  'Serbia',
  'Seychelles',
  'Sierra Leone',
  'Slovakia',
  'Slovenia',
  'Somalia',
  'South Sudan',
  'Spain',
  'Sri Lanka',
  'Sudan',
  'Suriname',
  'Sweden',
  'Switzerland',
  'Syria',
  'Taiwan',
  'Tajikistan',
  'Tanzania',
  'Thailand',
  'Timor-Leste',
  'Togo',
  'Trinidad and Tobago',
  'Tunisia',
  'Turkey',
  'Turkmenistan',
  'Tuvalu',
  'Uganda',
  'Ukraine',
  'United Arab Emirates',
  'Uruguay',
  'Uzbekistan',
  'Venezuela',
  'Vietnam',
  'Yemen',
  'Zambia',
  'Zimbabwe',
];

// ── Types ──────────────────────────────────────────────────────────────────

interface OtherName {
  familyName: string;
  firstName: string;
  middleNames: string;
}

// 2.4's columns, in order: the field each fills and the header it shows. ONE source for
// both the visible <th> and each cell's name — a <th> does not label an <input>, so each
// cell is named "Other name 2, Surname", the row plus its header's own words (UX-4 / WCAG
// 4.1.2), and the name cannot drift from what is on screen (WCAG 2.5.3; cca-piv70).
const OTHER_NAME_COLUMNS: readonly { field: keyof OtherName; header: string }[] = [
  { field: 'familyName', header: 'Surname' },
  { field: 'firstName', header: 'First name' },
  { field: 'middleNames', header: 'Middle names' },
];

interface FormState {
  // Section 1
  jobTitle: string;
  roleDescription: string;
  workingWith: string[];
  caringInHome: string;
  workerType: string;
  // No 1.7 / 1.8 state: neither is asked (ADR-067, amendment 2026-10-28; children-act.ts).
  previouslyVetted: string;
  sameRoleAsPrevious: string;
  // Section 2
  familyName: string;
  firstName: string;
  middleNames: string;
  gender: string;
  dateOfBirth: string;
  placeOfBirth: string;
  countryOfBirth: string;
  nzDriverLicence: string;
  addressStreet: string;
  addressSuburb: string;
  addressPostcode: string;
  addressTownCity: string;
  guardianName: string;
  otherNames: OtherName[];
  // E-sig
  esigName: string;
  esigEmail: string;
  esigAccepted: boolean;
  guardianEsigName: string;
  guardianEsigAccepted: boolean;
  esigNameManuallyEdited: boolean;
  // Paper transcription (Registrar only, ?paper=1)
  paperSignedDate: string;
  paperApproverName: string;
  transcriberName: string;
  wetSignatureFileLink: string;
}

const EMPTY: FormState = {
  jobTitle: '',
  roleDescription: '',
  workingWith: [],
  caringInHome: '',
  workerType: '',
  previouslyVetted: '',
  sameRoleAsPrevious: '',
  familyName: '',
  firstName: '',
  middleNames: '',
  gender: '',
  dateOfBirth: '',
  placeOfBirth: '',
  countryOfBirth: '',
  nzDriverLicence: '',
  addressStreet: '',
  addressSuburb: '',
  addressPostcode: '',
  addressTownCity: '',
  guardianName: '',
  otherNames: [{ familyName: '', firstName: '', middleNames: '' }],
  esigName: '',
  esigEmail: '',
  esigAccepted: false,
  guardianEsigName: '',
  guardianEsigAccepted: false,
  esigNameManuallyEdited: false,
  paperSignedDate: '',
  paperApproverName: '',
  transcriberName: '',
  wetSignatureFileLink: '',
};

// ── Helpers ────────────────────────────────────────────────────────────────

/**
 * Whole years of age on the NZ calendar day `onDay` (YYYY-MM-DD), or null
 * while the date of birth is empty or not YYYY-MM-DD (cca-080yb). The caller
 * picks the day (`ageDay` below): today, `stamp()`, Pacific/Auckland (UX-8),
 * never the browser's own day; or, on the paper route, the day the paper form
 * was signed. The date of birth is read from its own digits, never through
 * `new Date(dob)`, which is UTC midnight read back in the browser's zone (the
 * day before, anywhere west of UTC). The age is the difference in years, less
 * one when that day's (month, day) comes before the birthday's, so a 29
 * February birthday counts from 1 March in a common year.
 *
 * The twin of `ageOnNzDay` in cca-parishes-worker's src/vetting/nz-age.ts,
 * which the server's 14+ and guardian gates use, on the same day. The page
 * cannot import a worker, so the rule lives in both places. Change one,
 * change the other.
 */
function ageFromDob(dob: string, onDay: string): number | null {
  const born = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dob);
  const today = /^(\d{4})-(\d{2})-(\d{2})$/.exec(onDay);
  if (!born || !today) return null;
  const [by, bm, bd] = [Number(born[1]), Number(born[2]), Number(born[3])];
  const [ty, tm, td] = [Number(today[1]), Number(today[2]), Number(today[3])];
  const beforeBirthday = tm < bm || (tm === bm && td < bd);
  return ty - by - (beforeBirthday ? 1 : 0);
}

/** Today on the NZ calendar (UX-8: Intl with `timeZone`, never the browser's zone). */
function todayLabel(): string {
  return new Date().toLocaleDateString('en-NZ', {
    timeZone: 'Pacific/Auckland',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

function isValidLicence(v: string) {
  return /^[A-Z]{2}[0-9]{6}$/.test(v);
}

// The server's rule, @cca/auth normaliseEmail, which the page cannot import (cca-6v3z).
const EMAIL_SHAPE_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The longest 1.3 role description and 1.11 job title the routes accept
 *  (cca-49qxu): NZPVS's own 100, from its portal's field map (28 Oct 2026).
 *  Twins of the constants in cca-parishes-worker's intake-fields.ts, which the
 *  page cannot import. Change one, change the other. */
const ROLE_DESCRIPTION_MAX_LENGTH = 100;
const JOB_TITLE_MAX_LENGTH = 100;

/** From this many characters, a capped field's status line says it is near
 *  the cap (Miranda r1 M-1). */
const NEAR_LIMIT_AT = 90;

/** 1.3 or 1.11 past the Police system's cap, under its field on submit
 *  (Miranda r1 M-1). The box takes more, so nothing is cut off as it is typed. */
function overLimitRefusal(q: '1.3' | '1.11', length: number, max: number): string {
  return `${q} is ${length} characters; the Police system takes up to ${max}. Shorten it, then submit again.`;
}

/** Hidden on screen, read by a screen reader — the usual clip pattern. */
const SR_ONLY: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

/** A key of this form's own state, so a server refusal that names it can be
 *  shown under that field. `EMPTY` carries every key, so it is the list. */
function isFormField(field: unknown): field is keyof FormState {
  return typeof field === 'string' && Object.hasOwn(EMPTY, field);
}

/** The paper route's own refusals (cca-parishes-worker parish-dashboard.ts),
 *  word for word, so the page and the route say the same thing under the same
 *  field (cca-080yb, Miranda r2). Change one, change the other. */
const PAPER_UNDER_14 =
  'The applicant was under 14 on the date the paper form was signed. Check both dates against the paper form. If both are right, this form cannot be entered.';
const PAPER_GUARDIAN_REQUIRED =
  "The applicant was 14 or 15 on the date the paper form was signed, so a parent or guardian's name is needed. Check the paper form.";
const PAPER_SIGNED_BEFORE_DOB =
  "The date the paper form was signed is before the applicant's date of birth. Check both dates against the paper form, then submit again.";
/** The paper guardian refusal while the age is still judged on today (no
 *  usable signed date yet), the state a registrar transcribing top to bottom
 *  meets first (Miranda r3). The page's own: the route always has a signed
 *  date, so it says the sentence above. */
const PAPER_GUARDIAN_REQUIRED_TODAY =
  "The applicant is 14 or 15 today, so a parent or guardian's name is likely needed. Check the paper form; the date it was signed decides.";
/** What the signed date says under it when it puts an applicant who is 14 or
 *  more today under 14 on that date (Miranda r3): the route's under-14 order,
 *  standing on its own away from D.O.B. */
const PAPER_SIGNED_UNDER_14_NOTE =
  'On the date the paper form was signed, the applicant was under 14. Check both dates against the paper form — if both are right, this form cannot be entered.';
/** The public route's guardian refusals (cca-52uxp) — 2.3's name, then the
 *  guardian e-signature's two — word for word the consent route's own
 *  (cca-parishes-worker consent-form.ts GUARDIAN_REFUSALS). Change one, change
 *  the other. */
const GUARDIAN_NAME_REQUIRED = "Enter the parent or guardian's full name (section 2.3).";
const GUARDIAN_ESIG_NAME_REQUIRED = 'The parent or guardian must type their own full name to sign.';
const GUARDIAN_ESIG_TICK_REQUIRED = 'The parent or guardian must tick their consent box to submit.';
/** The email refusal, word for word as both intake routes send it
 *  (consent-form.ts, parish-dashboard.ts), so the banner reads the same
 *  whichever caught it (cca-6v3z). Change one, change all three. */
export const INVALID_EMAIL = 'Please enter a valid email address.';
/** The page's refusals that are whole sentences, naming what is wrong. When
 *  one of these is the only refusal, the top banner shows it, as it shows the
 *  route's own sentence when the route refuses (Miranda r3 J1). Any other
 *  refusal ("Required", "Must be 4 digits") makes sense only beside its field,
 *  so the banner keeps its generic words. The sentences that carry a value —
 *  a 2.4 refusal names its row, a 1.3 / 1.11 refusal its length — cannot be
 *  listed here; the banner checks those by field (cca-49qxu). */
const SENTENCE_REFUSALS: ReadonlySet<string> = new Set([
  PAPER_UNDER_14,
  PAPER_GUARDIAN_REQUIRED,
  PAPER_GUARDIAN_REQUIRED_TODAY,
  PAPER_SIGNED_BEFORE_DOB,
  GUARDIAN_NAME_REQUIRED,
  GUARDIAN_ESIG_NAME_REQUIRED,
  GUARDIAN_ESIG_TICK_REQUIRED,
  INVALID_EMAIL,
  OTHER_NAMES_MESSAGES.tooMany,
  OTHER_NAMES_MESSAGES.tooLong,
]);
/** The paper guardian name's input id: its label, and the signed date's link to it. */
const GUARDIAN_NAME_ID = 'guardian-name';

// ── Sub-components ─────────────────────────────────────────────────────────

// The asterisk is the SIGHTED cue only, so it is aria-hidden — a screen reader no longer
// hears "star" inside every name. A required control says so itself with
// `aria-required="true"`; never the `required` attribute, which would switch on the
// browser's own validation that `noValidate` keeps off (Miranda P1; cca-piv70).
function Req() {
  return (
    <span aria-hidden="true" style={{ color: 'var(--red)' }}>
      {' *'}
    </span>
  );
}
// A question answered by a GROUP of radios or checkboxes (1.4, 1.5, 1.6, 1.9) has no one
// control to carry aria-required, so its <legend> (ChoiceGroup) says "required" in words,
// heard and not seen (`datefield-sr`, @cca/ui's shared visually-hidden rule). It is the ONE
// carrier: the fieldset takes no aria-required and no radiogroup role is added to hold one,
// or the group would be heard saying it twice (aria-required means nothing on a group of
// checkboxes anyway). Never inside a <label for>: that control already says "required"
// through aria-required (Miranda r2, r3, r6; cca-piv70, cca-hv7b1).
function GroupReq() {
  return (
    <>
      <Req />
      <span className="datefield-sr">{' required'}</span>
    </>
  );
}
// `htmlFor` ties the visible label to its control by id (UX-4 / WCAG 1.3.1) — without it
// the <label> is only visual and a screen reader announces the input unnamed. REQUIRED, so
// typecheck enforces what the jsx-a11y lint cannot: the lint accepts `<label>{children}</label>`
// because a children expression MIGHT render a control (cca-piv70).
function FieldLabel({ children, htmlFor }: { children: React.ReactNode; htmlFor: string }) {
  return (
    <label
      htmlFor={htmlFor}
      style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 5 }}
    >
      {children}
    </label>
  );
}
function Hint({ children, id }: { children: React.ReactNode; id?: string }) {
  return (
    <p id={id} style={{ fontSize: 13, color: '#666', fontStyle: 'italic', margin: '0 0 8px' }}>
      {children}
    </p>
  );
}
/**
 * Under a field capped at the Police system's 100 (cca-49qxu, Miranda r1 M-1):
 * a visible "64 of 100", and a status line that speaks ONLY when the count
 * crosses into the last ten or past the cap — its words change at those two
 * points and nowhere else, so nothing is announced on each keystroke. The
 * field ties `id` (the count) into its aria-describedby.
 */
function CharCount({
  id,
  q,
  length,
  max,
}: {
  id: string;
  q: '1.3' | '1.11';
  length: number;
  max: number;
}) {
  const status =
    length > max
      ? `${q} is over ${max} characters, the most the Police system takes.`
      : length >= NEAR_LIMIT_AT
        ? `${q} is close to ${max} characters, the most the Police system takes.`
        : '';
  return (
    <>
      <p
        id={id}
        style={{
          fontSize: 12,
          color: length > max ? 'var(--red)' : 'var(--text2)',
          margin: '4px 0 0',
        }}
      >
        {`${length} of ${max}`}
      </p>
      <p role="status" data-char-limit={q} style={SR_ONLY}>
        {status}
      </p>
    </>
  );
}
// The question number's and the question's looks, shared by the plain heading (QNum, QLabel)
// and a choice group's <legend> (ChoiceGroup), so the two cannot drift apart.
const QNUM_STYLE = { fontSize: 12, fontWeight: 700, color: '#1e4d3f', marginBottom: 3 } as const;
const QLABEL_STYLE = { fontSize: 14, fontWeight: 600, marginBottom: 4 } as const;
function QNum({ n }: { n: string }) {
  return <div style={QNUM_STYLE}>{n}</div>;
}
// A question answered by ONE field (1.3, 1.11) passes `htmlFor`, and the visible question
// becomes that field's <label> — not its "e.g." placeholder (UX-4 / WCAG 1.3.1, 4.1.2;
// cca-piv70). A question that only shows something (1.1, 1.2, 1.12) stays a plain block. A
// question answered by radios or checkboxes is a ChoiceGroup instead.
function QLabel({ children, htmlFor }: { children: React.ReactNode; htmlFor?: string }) {
  return htmlFor ? (
    <label htmlFor={htmlFor} style={{ ...QLABEL_STYLE, display: 'block' }}>
      {children}
    </label>
  ) : (
    <div style={QLABEL_STYLE}>{children}</div>
  );
}
function QRow({ children }: { children: React.ReactNode }) {
  return <div style={{ marginBottom: 22 }}>{children}</div>;
}
/**
 * A question answered by a GROUP of radios or checkboxes (1.4, 1.5, 1.6, 1.9, 1.10): a native
 * <fieldset> whose <legend> is the question — its number, its words and, when required,
 * GroupReq's hidden "required" (WCAG 1.3.1, H71; Miranda r6 S1; cca-hv7b1). Moving by form
 * control (VoiceOver VO-Cmd-J, NVDA F or Tab), a screen reader says the question on entering
 * the group, not "Yes, radio button, 1 of 2" alone. Each choice is still named by its own
 * wrapping <label>.
 *
 * Nothing moves on screen. The fieldset loses its frame, padding, margin and min-content
 * width (that width would break reflow at 320px), and the legend its padding. The legend's
 * number and question carry QNum's and QLabel's styles, as <span>s set to block, because a
 * legend holds phrasing content only; the space between them keeps the two apart in the
 * group's name.
 *
 * The hint and the error are tied to the group: the fieldset's aria-describedby names the
 * hint, then the error, each only while it is on screen — as the forms renderer's `radio`
 * branch ties help and error alike (SchemaField.tsx; four of these five groups are radios),
 * and as the GOV.UK radios and checkboxes pattern does. The error is placed as GOV.UK places
 * it, inside the group: legend, hint, error, choices. SchemaField's `checkboxes` branch
 * leaves the error out of describedby; that exclusion is not kept here, because without the
 * tie, moving by form control (Tab, VO-Cmd-J) goes from the legend straight to the first
 * choice, and the hint and the error were never heard (WCAG 1.3.1 ARIA1, 3.3.2; Miranda r7
 * S1). The accepted cost: a screen reader reading line by line (NVDA) hears the error twice,
 * once in place and once as the description. Only the tie follows the `radio` branch: that
 * branch also hides its help while an error shows, and adds role="radiogroup", aria-invalid
 * and aria-required; ChoiceGroup does none of these. The hint stays on screen with the error.
 *
 * The error opens with a visually hidden "Error: ", as GOV.UK's does, so it is heard as a
 * refusal and not in a hint's voice, both in place and through the description: "<hint>
 * Error: Select Yes or No" (Miranda r8 S1). Seen, the words alone.
 *
 * Still ONE carrier for "required": the legend's hidden word. The fieldset takes no
 * aria-required and no role, and no aria-invalid, which means nothing on a plain group.
 */
function ChoiceGroup({
  n,
  question,
  required = false,
  hint,
  error,
  errorId,
  children,
}: {
  n: string;
  question: string;
  required?: boolean;
  /** Shown under the question, and named first in the group's aria-describedby. */
  hint?: { id: string; text: string };
  error?: string | undefined;
  errorId?: string;
  children: React.ReactNode;
}) {
  // Only what is on screen: a token for an element that is not rendered is dropped by a
  // screen reader, and would leave the reference sweep with nothing to land on.
  const describedBy =
    [hint?.id, error && errorId ? errorId : undefined].filter(Boolean).join(' ') || undefined;
  return (
    <QRow>
      <fieldset
        style={{ border: 0, padding: 0, margin: 0, minInlineSize: 0 }}
        aria-describedby={describedBy}
      >
        <legend style={{ padding: 0, width: '100%' }}>
          <span style={{ ...QNUM_STYLE, display: 'block' }}>{n}</span>{' '}
          <span style={{ ...QLABEL_STYLE, display: 'block', marginBottom: 0 }}>
            {question}
            {required && <GroupReq />}
          </span>
        </legend>
        {/* A legend lays out its contents on their own, so the question's 4px below no longer
            folds into what follows, as the plain heading's did. This box gives it back: its
            top margin folds with its first child's, so a hint or an error sits 4px under the
            question and the choices 6px, as before. */}
        <div style={{ marginTop: QLABEL_STYLE.marginBottom }}>
          {hint && <Hint id={hint.id}>{hint.text}</Hint>}
          {error && (
            <p id={errorId} style={{ color: 'var(--red)', fontSize: 12, margin: '0 0 4px' }}>
              <span className="datefield-sr">{'Error: '}</span>
              {error}
            </p>
          )}
          {children}
        </div>
      </fieldset>
    </QRow>
  );
}
function SectionCard({
  pill,
  title,
  children,
}: {
  pill: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        background: 'white',
        border: '1px solid #e0e0d8',
        borderRadius: 8,
        padding: 28,
        marginBottom: 24,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 12,
          marginBottom: 20,
          paddingBottom: 12,
          borderBottom: '2px solid #1e4d3f',
        }}
      >
        <span
          style={{
            background: '#1e4d3f',
            color: 'white',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.05em',
            padding: '3px 8px',
            borderRadius: 3,
          }}
        >
          {pill}
        </span>
        <span style={{ fontSize: 16, fontWeight: 600 }}>{title}</span>
      </div>
      {children}
    </div>
  );
}
function Radio({
  name,
  value,
  checked,
  onChange,
  children,
}: {
  name: string;
  value: string;
  checked: boolean;
  onChange: (v: string) => void;
  children: React.ReactNode;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        cursor: 'pointer',
        fontSize: 14,
        marginBottom: 0,
      }}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={() => onChange(value)}
        style={{
          width: 17,
          height: 17,
          marginTop: 2,
          accentColor: '#1e4d3f',
          flexShrink: 0,
          cursor: 'pointer',
        }}
      />
      {children}
    </label>
  );
}
function CheckItem({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        cursor: 'pointer',
        fontSize: 14,
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{
          width: 17,
          height: 17,
          marginTop: 2,
          accentColor: '#1e4d3f',
          flexShrink: 0,
          cursor: 'pointer',
        }}
      />
      {children}
    </label>
  );
}
// font-size deliberately NOT inline: it lives in global.css (`.nzpv-form` — 15px
// desktop, 16px under the shared 640px breakpoint per mobile.md MOB-2) because an
// inline style can't be media-queried away on phones.
const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  border: '1px solid #ccc',
  borderRadius: 4,
  fontFamily: 'inherit',
  color: '#1a1a1a',
  background: 'white',
  boxSizing: 'border-box',
};
const otherNameHeaderStyle: React.CSSProperties = {
  textAlign: 'left',
  fontSize: 12,
  fontWeight: 600,
  color: '#666',
  padding: '6px 8px',
  borderBottom: '1px solid #ddd',
  background: '#f7f7f7',
};

// ── Main component ─────────────────────────────────────────────────────────

type PageState = 'loading' | 'ready' | 'submitting' | 'success' | 'error' | 'parish_error';

export function VettingFormPage(): JSX.Element {
  const { parishSlug } = useParams<{ parishSlug: string }>();
  const [searchParams] = useSearchParams();
  // Print-transition: the Registrar transcribes a wet-ink paper form. The
  // worker endpoint is session-gated to the Registrar; the flag only shapes
  // the UI (attestation block instead of applicant/guardian e-signatures).
  const isPaper = searchParams.get('paper') === '1';
  // On the paper route the registrar types SOMEONE ELSE's details from their paper form,
  // so the applicant's identity fields take fieldProps' `proxy` option — autofill off: the
  // browser neither offers the registrar's own saved name, email or address there, nor keeps
  // the applicant's in the registrar's form history (Lazo L1 / Miranda S1; cca-piv70). On the
  // public route the applicant is filling in their own, and the tokens stay. The transcriber
  // field is the one that IS the person at the keyboard, so it never takes this.
  const applicantAutofill = { proxy: isPaper };
  // Invitation links carry the recipient's email + first name so the
  // authorisation block arrives pre-populated (still editable).
  const [form, setForm] = useState<FormState>(() => {
    const email = searchParams.get('email')?.trim() ?? '';
    const firstName = searchParams.get('name')?.trim() ?? '';
    return {
      ...EMPTY,
      ...(email ? { esigEmail: email } : {}),
      ...(firstName ? { firstName, esigName: firstName } : {}),
    };
  });
  const [pageState, setPageState] = useState<PageState>('loading');
  const [errors, setErrors] = useState<
    Partial<Record<keyof FormState | 'working_with' | 'licence', string>>
  >({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  // The 1-based 2.4 row the page's own refusal is about, so that row's boxes
  // are marked and tied to the sentence (Miranda r1 M-3); null for none.
  const [otherNamesRow, setOtherNamesRow] = useState<number | null>(null);
  const [verifyUrl, setVerifyUrl] = useState<string | null>(null);
  const [licenceError, setLicenceError] = useState(false);
  const [parishName, setParishName] = useState('');
  // The paper route (registrar only) answers 409 `identity_review_required`
  // with the SAME candidates payload the dashboard queue uses, having written
  // NOTHING (ADR-064). The chooser opens inline and the form is re-POSTed with
  // `identityDecision`, so the one person who can resolve it is already here.
  const [identityReview, setIdentityReview] = useState<{
    payload: CandidatesPayload;
    error: string | null;
  } | null>(null);
  const [identityBusy, setIdentityBusy] = useState<'link' | 'create' | null>(null);
  const identityRef = useRef<HTMLDivElement | null>(null);
  // 2.3's guardian name, which the signed date's status line links to (paper route).
  const guardianNameRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    fetch(`/api/vetting/form/${parishSlug}`)
      .then(async (res) => {
        if (!res.ok) {
          setPageState('parish_error');
          return;
        }
        const d = (await res.json().catch(() => null)) as { parishName?: string } | null;
        setParishName(d?.parishName ?? '');
        setPageState('ready');
      })
      .catch(() => setPageState('parish_error'));
  }, [parishSlug]);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  // Auto-sync esig name from name fields
  function syncEsig(patch: Partial<FormState>) {
    setForm((f) => {
      const next = { ...f, ...patch };
      if (!next.esigNameManuallyEdited) {
        const parts = [next.firstName, next.middleNames, next.familyName].filter(Boolean);
        next.esigName = parts.join(' ');
      }
      return next;
    });
  }

  // Any change to 2.4 — an edit, an add or a remove — clears its refusal and
  // the refused-row mark (Miranda r2). The mark is a row number taken at
  // submit: once a row above it is removed the names move up, and it would
  // mark a different row. The next submit checks the rows afresh.
  function setOtherNames(next: FormState['otherNames']) {
    set('otherNames', next);
    setOtherNamesRow(null);
    setErrors((prev) => {
      if (!prev.otherNames) return prev;
      const cleared = { ...prev };
      delete cleared.otherNames;
      return cleared;
    });
  }

  // The day the age is judged on, the server's own (cca-080yb, Lazo r1). On
  // the paper route that is the day the paper form was signed, once it is a
  // set date not after today and not before the date of birth: until then,
  // today, and the guardian field shows or hides again as the signed date is
  // entered or changed. A signed date before the birth is that date's own
  // mistake, which validate() and the route name under it, never an age (Tyson r2): a
  // 2006-for-2026 typo would otherwise be refused under D.O.B as under 14, and
  // a date input passes through 0002-…, 0020-…, 0202-… as its year is typed,
  // which flicked the guardian field off and on. (With no date of birth there
  // is no age to judge, whichever day.) On the applicant's route, signing IS
  // today.
  const signedDay = form.paperSignedDate;
  const judgedOnSignedDay =
    isPaper &&
    /^\d{4}-\d{2}-\d{2}$/.test(signedDay) &&
    signedDay <= stamp() &&
    signedDay >= form.dateOfBirth;
  const ageDay = judgedOnSignedDay ? signedDay : stamp();
  const age = ageFromDob(form.dateOfBirth, ageDay);
  const isMinor = age !== null && (age === 14 || age === 15);
  // The signed date is entered in the Authorisation box at the foot of the
  // page, out of sight of 2.3 and D.O.B (Miranda r2 S1). When it changes what
  // they say, against judging on today, a line under it says so: it opens the
  // guardian box (14 or 15 that day, 16 or more today, since the date is not
  // after today), or it puts an applicant who is 14 or more today under 14 that
  // day, which closes a box open today or, at 16 or more today, is a refusal
  // under D.O.B with no box to close (Miranda r3). Under 14 on both days, the
  // refusal is the same either way, and nothing is said.
  const ageToday = ageFromDob(form.dateOfBirth, stamp());
  const minorToday = ageToday === 14 || ageToday === 15;
  const signedDayChange: 'opens' | 'under14' | null = !judgedOnSignedDay
    ? null
    : isMinor && !minorToday
      ? 'opens'
      : age !== null && age < 14 && ageToday !== null && ageToday >= 14
        ? 'under14'
        : null;

  function validate(): boolean {
    const e: typeof errors = {};
    // 1.3 and 1.11 take more than the Police system's 100 as they are typed
    // (the count says so); a submit past it is refused with its length
    // (Miranda r1 M-1). The routes refuse it too.
    if (!form.jobTitle.trim()) e.jobTitle = 'Required';
    else if (form.jobTitle.length > JOB_TITLE_MAX_LENGTH)
      e.jobTitle = overLimitRefusal('1.11', form.jobTitle.length, JOB_TITLE_MAX_LENGTH);
    if (!form.roleDescription.trim()) e.roleDescription = 'Required';
    else if (form.roleDescription.length > ROLE_DESCRIPTION_MAX_LENGTH)
      e.roleDescription = overLimitRefusal(
        '1.3',
        form.roleDescription.length,
        ROLE_DESCRIPTION_MAX_LENGTH,
      );
    // A group's legend already ends "… required", so its error says what to do rather than
    // "Required" again (UX-6; Miranda r7 S2). Not whole sentences — none names its question —
    // so none is in SENTENCE_REFUSALS, and the banner keeps its general words.
    if (form.workingWith.length === 0) e.working_with = 'Select at least one';
    if (!form.caringInHome) e.caringInHome = 'Select Yes or No';
    if (!form.workerType) e.workerType = 'Select one';
    if (!form.previouslyVetted) e.previouslyVetted = 'Select Yes or No';
    if (!form.familyName.trim()) e.familyName = 'Required';
    if (!form.firstName.trim()) e.firstName = 'Required';
    if (!form.gender) e.gender = 'Required';
    if (!form.dateOfBirth) e.dateOfBirth = 'Required';
    if (!form.placeOfBirth.trim()) e.placeOfBirth = 'Required';
    if (!form.countryOfBirth.trim()) e.countryOfBirth = 'Required';
    if (!form.addressStreet.trim()) e.addressStreet = 'Required';
    if (!/^\d{4}$/.test(form.addressPostcode.trim())) e.addressPostcode = 'Must be 4 digits';
    if (!form.addressTownCity.trim()) e.addressTownCity = 'Required';
    if (!form.esigName.trim()) e.esigName = 'Required';
    if (!form.esigEmail.trim()) e.esigEmail = 'Required';
    else if (!EMAIL_SHAPE_RE.test(form.esigEmail.trim())) e.esigEmail = INVALID_EMAIL;
    if (!form.esigAccepted) e.esigAccepted = 'You must tick this box to submit';
    if (isPaper) {
      // NZ calendar dates, not instants (UX-8): `new Date('YYYY-MM-DD')` is UTC
      // midnight, so a form signed today read as "in the future" until 1pm NZDT
      // (noon NZST).
      // `stamp()` is today's Pacific/Auckland date as YYYY-MM-DD, and two such
      // strings compare in date order. The input's `max` is the same `stamp()`.
      if (!form.paperSignedDate) e.paperSignedDate = 'Required';
      else if (form.paperSignedDate > stamp())
        e.paperSignedDate = 'Must be today or earlier — check the date on the paper form';
      // Nor before the date of birth, in the route's own words (Miranda r2 J1),
      // so it is refused here with the rest and not only by the server. As on
      // the route, a date of birth after today is that date's mistake alone.
      else if (
        form.dateOfBirth &&
        form.dateOfBirth <= stamp() &&
        form.paperSignedDate < form.dateOfBirth
      )
        e.paperSignedDate = PAPER_SIGNED_BEFORE_DOB;
      if (!form.paperApproverName.trim()) e.paperApproverName = 'Required';
      if (!form.transcriberName.trim()) e.transcriberName = 'Required';
    }
    // On the paper route the refusal speaks as the guardian box does: of the
    // signed date once that is the day judged on (the route's own sentence),
    // and of today until then (Miranda r3). A name holds a letter, as the
    // consent route requires (cca-52uxp).
    if (isMinor && !/\p{L}/u.test(form.guardianName))
      e.guardianName = isPaper
        ? judgedOnSignedDay
          ? PAPER_GUARDIAN_REQUIRED
          : PAPER_GUARDIAN_REQUIRED_TODAY
        : GUARDIAN_NAME_REQUIRED;
    // The guardian's own e-signature (cca-52uxp): their typed name, holding a
    // letter, and their own tick, as the route requires. A paper guardian signed
    // the original in ink.
    if (isMinor && !isPaper) {
      if (!/\p{L}/u.test(form.guardianEsigName)) e.guardianEsigName = GUARDIAN_ESIG_NAME_REQUIRED;
      if (!form.guardianEsigAccepted) e.guardianEsigAccepted = GUARDIAN_ESIG_TICK_REQUIRED;
    }
    if (form.nzDriverLicence.trim() && !isValidLicence(form.nzDriverLicence.trim()))
      e.licence = 'Must be 2 letters + 6 digits (e.g. AB123456)';
    // 2.4 (cca-49qxu): the same rule, in the same words, as the routes — and
    // the row it is about, whose boxes are marked (Miranda r1 M-3).
    const namesProblem = otherNamesProblem(form.otherNames, form);
    if (namesProblem) e.otherNames = namesProblem.message;
    setOtherNamesRow(namesProblem?.row ?? null);
    // A date of birth after today is said as that, before any age: its age is
    // negative, and "Must be 14" would send the applicant (or the registrar)
    // looking for the wrong mistake, when it is a mistyped year or a year wheel
    // flicked past today. The same NZ-day comparison as the signed date above,
    // and the input's `max` is the same `stamp()`.
    // On the paper route the age is the signer's on the signed date (`ageDay`),
    // and an applicant under 14 today was under 14 on any signed date not
    // after today, so the paper wording holds whenever `ageDay` falls back to
    // today too.
    if (form.dateOfBirth > stamp())
      e.dateOfBirth = 'Must be today or earlier — check the date of birth';
    else if (age !== null && age < 14)
      e.dateOfBirth = isPaper ? PAPER_UNDER_14 : 'Must be 14 years of age or over';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  /** The typed side of the identity comparison — what this form says, never
   *  rewritten from a CRM record (registrar ruling 2). */
  function typedApplication() {
    const givenNames = [form.firstName, form.middleNames].filter(Boolean).join(' ');
    return {
      applicantName: [givenNames, form.familyName].filter(Boolean).join(' ').trim(),
      applicantEmail: form.esigEmail.trim(),
      dateOfBirthSubmitted: form.dateOfBirth,
      jobTitle: form.jobTitle,
      townCity: form.addressTownCity,
    };
  }

  function buildBody() {
    const givenNames = [form.firstName, form.middleNames].filter(Boolean).join(' ');
    return {
      parishSlug,
      jobTitle: form.jobTitle,
      roleDescription: form.roleDescription,
      workingWith: form.workingWith,
      caringInHome: form.caringInHome === 'yes',
      workerType: form.workerType,
      // 1.7 is not asked: always No for CCAANZ (ADR-067, amendment 2026-10-28), so
      // no 1.8 category is ever sent.
      isChildrensWorker: IS_CHILDRENS_WORKER,
      previouslyVetted: form.previouslyVetted === 'yes',
      ...(form.previouslyVetted === 'yes' && form.sameRoleAsPrevious
        ? { sameRoleAsPrevious: form.sameRoleAsPrevious === 'yes' }
        : {}),
      familyName: form.familyName,
      givenNames,
      ...(form.middleNames.trim() ? { middleNames: form.middleNames.trim() } : {}),
      gender: form.gender,
      dateOfBirth: form.dateOfBirth,
      placeOfBirth: form.placeOfBirth,
      countryOfBirth: form.countryOfBirth,
      ...(form.nzDriverLicence.trim() ? { nzDriverLicence: form.nzDriverLicence.trim() } : {}),
      addressStreet: form.addressStreet,
      ...(form.addressSuburb.trim() ? { addressSuburb: form.addressSuburb } : {}),
      addressPostcode: form.addressPostcode,
      addressTownCity: form.addressTownCity,
      ...(isMinor && form.guardianName ? { guardianName: form.guardianName } : {}),
      // Every row with anything typed — validate() has made sure each has a
      // family name — so a row holding only middle names is no longer dropped.
      otherNames: form.otherNames.filter((n) => !isBlankOtherName(n)),
      esigName: form.esigName,
      esigEmail: form.esigEmail,
      esigAccepted: form.esigAccepted,
      // Exactly as the guardian ticked it, never a forced true (cca-52uxp).
      ...(isMinor && !isPaper
        ? {
            guardianEsigName: form.guardianEsigName,
            guardianEsigAccepted: form.guardianEsigAccepted,
          }
        : {}),
      ...(isPaper
        ? {
            paperSignedDate: form.paperSignedDate,
            paperApproverName: form.paperApproverName.trim(),
            transcriberName: form.transcriberName.trim(),
            attested: form.esigAccepted,
            ...(form.wetSignatureFileLink.trim()
              ? { wetSignatureFileLink: form.wetSignatureFileLink.trim() }
              : {}),
          }
        : {}),
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    };
  }

  /**
   * Submit, or re-submit carrying the registrar's identity decision.
   *
   * Every submission now gets the SAME answer whether or not it was held
   * (registrar ruling R3): a held application is no longer announced on screen,
   * because a screen that changes only when an email is already on file tells
   * anyone who types an address whether that address is on file.
   */
  async function postApplication(decision?: IdentityChoice) {
    if (decision) setIdentityBusy(decision.action);
    else {
      setPageState('submitting');
      setSubmitError(null);
    }

    const endpoint = isPaper
      ? `/api/vetting/parish/${parishSlug}/paper-application`
      : '/api/vetting/form';
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'include',
        body: JSON.stringify({
          ...buildBody(),
          ...(decision
            ? {
                identityDecision: {
                  action: decision.action,
                  ...(decision.contactId ? { contactId: decision.contactId } : {}),
                  version: decision.version,
                },
              }
            : {}),
        }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        status?: string;
        verifyUrl?: string;
        message?: string;
        error?: string;
        field?: string;
      };
      if (!res.ok) {
        // A paper hold, or a version that went stale while the registrar chose:
        // both answer 409 with the shared candidates payload, and NOTHING has
        // been written either way.
        const identity = classifyPaperIdentityError({ status: res.status, data });
        if (identity) {
          setIdentityReview({
            payload: identity.payload,
            error: identity.kind === 'record_changed' ? PAPER_RECORD_CHANGED : null,
          });
          setSubmitError(null);
          setPageState('ready');
          window.setTimeout(() => identityRef.current?.focus(), 0);
          return;
        }
        const authMsg =
          isPaper && (res.status === 401 || res.status === 403)
            ? 'Paper transcription requires a Registrar session — sign in to the vetting portal first.'
            : null;
        // A refusal that names a field of this form and says why: the sentence
        // goes under that field too, where the registrar is looking (UX-6,
        // WCAG 2.2 SC 3.3.1). The paper route's `invalid_field` dates, and its
        // `applicant_too_young` and `guardian_consent_required` (cca-080yb),
        // which keep their own codes; so the code is not what decides.
        const field = data.field;
        const message = data.message;
        if (res.status === 400 && message && isFormField(field)) {
          setErrors((prev) => {
            const next = { ...prev };
            next[field] = message;
            return next;
          });
        }
        // The banner shows a sentence, never an error key: a body with no
        // `message` gets the generic one.
        setSubmitError(authMsg ?? (message || 'Submission failed. Please try again.'));
        setPageState('ready');
      } else {
        setIdentityReview(null);
        setVerifyUrl(data.verifyUrl ?? null);
        setPageState('success');
      }
    } catch {
      setSubmitError('A network error occurred. Please check your connection and try again.');
      setPageState('ready');
    } finally {
      setIdentityBusy(null);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) {
      // Motion is a preference (UX-5): a reduced-motion reader gets a jump.
      const reduced =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
      return;
    }
    await postApplication();
  }

  // ── Render states ──────────────────────────────────────────────────────────

  if (pageState === 'loading') {
    return (
      <AuthLayout>
        <p style={{ textAlign: 'center', color: 'var(--text2)', marginTop: 48 }}>Loading…</p>
      </AuthLayout>
    );
  }

  if (pageState === 'parish_error') {
    return (
      <AuthLayout>
        <div style={{ textAlign: 'center', marginTop: 48 }}>
          <p style={{ fontWeight: 600, marginBottom: 8 }}>Form not available</p>
          <p style={{ fontSize: 14, color: 'var(--text2)' }}>
            This link is not active. Please contact your parish for a current link.
          </p>
        </div>
      </AuthLayout>
    );
  }

  if (pageState === 'success') {
    return (
      <AuthLayout>
        <div style={{ maxWidth: 520, margin: '48px auto', textAlign: 'center' }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              background: '#1e4d3f',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: 28,
              color: 'white',
            }}
          >
            ✓
          </div>
          <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>
            {isPaper ? 'Paper application entered' : 'Application received'}
          </h2>
          {isPaper ? (
            <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 24 }}>
              The application is approved on the basis of the signed paper original and now sits in
              your lodgement queue.{' '}
              <a href="/registrar" style={{ color: '#1e4d3f', fontWeight: 600 }}>
                Back to the Registrar dashboard →
              </a>
            </p>
          ) : (
            /* ONE neutral screen for every submission, held or not (R3): a
               screen that changed only when an application was held would tell
               anyone who types an email whether that address is already on
               file. The difference lives in the emailed receipt, which only the
               mailbox owner sees. */
            <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 24 }}>
              We've emailed a confirmation to {form.esigEmail.trim() || 'the address you gave'}. It
              explains what happens next.
            </p>
          )}
          {verifyUrl && (
            <p style={{ fontSize: 13, color: 'var(--text2)' }}>
              You can verify your e-signature at any time:{' '}
              {/* The shared .btn-link: the AA link tone AND an underline, so the
                  link is not told apart from its sentence by colour alone
                  (WCAG 2.2 SC 1.4.1), the same move as LandingPage.tsx. */}
              <a href={verifyUrl} className="btn-link">
                {verifyUrl}
              </a>
            </p>
          )}
        </div>
      </AuthLayout>
    );
  }

  const busy = pageState === 'submitting';
  // 2.4's "+ Add another name" at the row cap (Miranda r1 M-13): off, and
  // looking off, with the cap's sentence under 2.4 — unless 2.4's refusal is
  // already that sentence.
  const atNamesCap = form.otherNames.length >= OTHER_NAMES_MAX_ROWS;
  const addNameOff = busy || atNamesCap;
  const namesCapNote = atNamesCap && errors.otherNames !== OTHER_NAMES_MESSAGES.tooMany;
  // The banner's words after a refused submit: the server's sentence when the
  // server refused (postApplication); when the page refused, the one refusal's
  // own sentence if there is exactly one and it is a whole sentence (Miranda r3
  // J1), as the server's would be; otherwise the generic words.
  const refusals = Object.values(errors);
  const onlyRefusal = refusals.length === 1 ? refusals[0] : undefined;
  // A whole sentence: the fixed ones, every 2.4 refusal (it names its row),
  // and 1.3 / 1.11 past the cap (it gives the length) — those two fields'
  // only other refusal is 'Required' (cca-49qxu).
  const isSentence = (s: string): boolean =>
    SENTENCE_REFUSALS.has(s) ||
    s === errors.otherNames ||
    ((s === errors.roleDescription || s === errors.jobTitle) && s !== 'Required');
  const bannerText =
    submitError ??
    (onlyRefusal && isSentence(onlyRefusal)
      ? onlyRefusal
      : 'Some required fields are missing or invalid. Please review and try again.');

  // ── Form ──────────────────────────────────────────────────────────────────

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f5f5f0',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header — matches approved consent-form-draft.html design */}
      <div style={{ background: '#1e4d3f', color: 'white', padding: '20px 32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <a
            href={CCA_HOME_URL}
            target="_blank"
            rel="noopener noreferrer"
            title="Confessing Anglicans Aotearoa New Zealand — opens in a new tab"
            style={{ display: 'flex', flexShrink: 0, lineHeight: 0 }}
          >
            <img
              src={ccaIconRGB}
              alt="CCAANZ"
              style={{ width: 55, height: 55, flexShrink: 0, filter: 'brightness(0) invert(1)' }}
            />
          </a>
          <div>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>
              New Zealand Police Vetting — Request &amp; Consent Form
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: 13, opacity: 0.8 }}>
              Church of Confessing Anglicans Aotearoa New Zealand (CCAANZ)
            </p>
          </div>
        </div>
      </div>

      <form
        onSubmit={(e) => void handleSubmit(e)}
        noValidate
        className="nzpv-form"
        style={{ maxWidth: 800, margin: '0 auto', width: '100%', padding: '32px 16px', flex: 1 }}
      >
        {/* Paper-transcription banner */}
        {isPaper && (
          <div
            style={{
              background: '#fff8e6',
              border: '1px solid #f0c040',
              borderRadius: 8,
              padding: '14px 18px',
              fontSize: 14,
              marginBottom: 24,
              lineHeight: 1.6,
            }}
          >
            <strong>Paper transcription — Registrar only.</strong> You are entering an application
            from a paper form signed in ink by the applicant and approver. Transcribe it exactly as
            written; your attestation at the end records the basis of consent, and the original is
            lodged with NZ Police by you.
          </div>
        )}

        {/* Identity review, inline (paper route, registrar only). Nothing has
            been written; the same chooser the dashboard queue uses decides who
            this application belongs to, and the form is submitted again with
            that decision. Correcting a typo above and submitting is the other
            way out. */}
        {identityReview && (
          <div
            className="idr-paper"
            ref={identityRef}
            tabIndex={-1}
            role="group"
            aria-labelledby="idr-paper-head"
          >
            <h2 id="idr-paper-head" className="idr-paper-head">
              {PAPER_REVIEW_HEADING}
            </h2>
            <p className="idr-paper-body">{PAPER_REVIEW_BODY}</p>
            <IdentityChooser
              typed={typedApplication()}
              parishName={parishName || (parishSlug ?? '')}
              payload={identityReview.payload}
              busy={identityBusy}
              error={identityReview.error}
              onChoose={(choice) => void postApplication(choice)}
            />
          </div>
        )}

        {/* Error banner. Mounted only while there is an error, so `role="alert"`
            announces it when it appears, after a refused submit, and never
            announces an empty box (WCAG 2.2 SC 4.1.3). Focus stays put; the
            identity chooser above moves focus for its own case. */}
        {(submitError ?? Object.keys(errors).length > 0) && (
          <div
            role="alert"
            style={{
              background: '#fef2f2',
              border: '1px solid var(--red)',
              borderRadius: 8,
              padding: '14px 18px',
              fontSize: 13,
              color: 'var(--red)',
              marginBottom: 24,
            }}
          >
            {bannerText}
          </div>
        )}

        {/* Preamble */}
        <div
          style={{
            background: 'white',
            border: '1px solid #e0e0d8',
            borderRadius: 8,
            padding: 28,
            marginBottom: 24,
          }}
        >
          <div
            style={{
              background: '#f0f4f0',
              borderLeft: '3px solid #1e4d3f',
              padding: '14px 16px',
              borderRadius: '0 4px 4px 0',
              fontSize: 14,
            }}
          >
            <p style={{ margin: '0 0 8px' }}>
              Complete <strong>Section 1</strong> (about your role) and <strong>Section 2</strong>{' '}
              (your personal details and consent). Your parish representative will complete identity
              verification separately.
            </p>
            <p style={{ margin: 0 }}>
              Fields marked <span style={{ color: 'var(--red)' }}>*</span> are required.
            </p>
          </div>
        </div>

        {/* ── SECTION 1 ── */}
        <SectionCard pill="SECTION 1" title="General Information">
          <QRow>
            <QNum n="1.1" />
            <QLabel>Agency</QLabel>
            <div
              style={{
                background: '#f7f7f7',
                border: '1px solid #e0e0d8',
                borderRadius: 4,
                padding: '12px 16px',
                fontSize: 14,
              }}
            >
              Church of Confessing Anglicans Aotearoa New Zealand (CCAANZ)
            </div>
            <p
              style={{
                fontSize: 12,
                color: 'var(--text2)',
                fontStyle: 'italic',
                margin: '6px 0 0',
              }}
            >
              Pre-filled by the system.
            </p>
          </QRow>

          <QRow>
            <QNum n="1.2" />
            <QLabel>Name of vetting subject</QLabel>
            <p style={{ fontSize: 13, color: '#666', margin: 0 }}>
              Populated from your name entered in Section 2.
            </p>
          </QRow>

          <QRow>
            <QNum n="1.3" />
            <QLabel htmlFor="role-description">
              Description of the role
              <Req />
            </QLabel>
            {/* Tied to the box too, first (Miranda r2): what to write, then how long. */}
            <Hint id="role-description-hint">
              Brief description used by Police to determine the type of vet required.
            </Hint>
            {/* No hard maxlength (Miranda r1 M-1): a box that stops taking
                characters cuts a sentence off without saying so. The count
                says how many, and a submit past 100 is refused with the length. */}
            <Hint id="role-description-limit">
              Up to {ROLE_DESCRIPTION_MAX_LENGTH} characters — the Police system takes no more.
            </Hint>
            <textarea
              id="role-description"
              name="role_description"
              aria-required="true"
              value={form.roleDescription}
              onChange={(e) => set('roleDescription', e.target.value)}
              rows={2}
              style={{ ...inputStyle }}
              disabled={busy}
              placeholder="e.g. Weekly supervision of children aged 5–12 during Sunday School sessions"
              aria-invalid={errors.roleDescription ? true : undefined}
              aria-describedby={[
                'role-description-hint',
                'role-description-limit',
                'role-description-count',
                ...(errors.roleDescription ? ['role-description-error'] : []),
              ].join(' ')}
            />
            <CharCount
              id="role-description-count"
              q="1.3"
              length={form.roleDescription.length}
              max={ROLE_DESCRIPTION_MAX_LENGTH}
            />
            {errors.roleDescription && (
              <p
                id="role-description-error"
                style={{ color: 'var(--red)', fontSize: 12, margin: '4px 0 0' }}
              >
                {errors.roleDescription}
              </p>
            )}
          </QRow>

          {/* 1.4, 1.5, 1.6, 1.9 and 1.10 are each a ChoiceGroup: a fieldset named by its
              question, described by its hint and error (WCAG 1.3.1, H71, ARIA1; cca-hv7b1). */}
          <ChoiceGroup
            n="1.4"
            question="Which groups will the vetting subject work with?"
            required
            error={errors.working_with}
            errorId="working-with-error"
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
              {['Children/Young People', 'Vulnerable Adults'].map((v) => (
                <CheckItem
                  key={v}
                  checked={form.workingWith.includes(v)}
                  onChange={(checked) =>
                    set(
                      'workingWith',
                      checked ? [...form.workingWith, v] : form.workingWith.filter((x) => x !== v),
                    )
                  }
                >
                  {v === 'Children/Young People' ? 'Children/Young people' : 'Vulnerable adults'}
                </CheckItem>
              ))}
            </div>
          </ChoiceGroup>

          <ChoiceGroup
            n="1.5"
            question="Does the role involve caring for people in the vetting subject's home?"
            required
            hint={{
              id: 'caring-in-home-hint',
              text: 'Whether vulnerable children or adults visit the home of the person being vetted for support.',
            }}
            error={errors.caringInHome}
            errorId="caring-in-home-error"
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
              <Radio
                name="caring_in_home"
                value="yes"
                checked={form.caringInHome === 'yes'}
                onChange={(v) => set('caringInHome', v)}
              >
                Yes
              </Radio>
              <Radio
                name="caring_in_home"
                value="no"
                checked={form.caringInHome === 'no'}
                onChange={(v) => set('caringInHome', v)}
              >
                No
              </Radio>
            </div>
          </ChoiceGroup>

          <ChoiceGroup
            n="1.6"
            question="Is the vetting subject:"
            required
            error={errors.workerType}
            errorId="worker-type-error"
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
              <Radio
                name="worker_type"
                value="Paid Worker"
                checked={form.workerType === 'Paid Worker'}
                onChange={(v) => set('workerType', v)}
              >
                A paid worker
              </Radio>
              <Radio
                name="worker_type"
                value="Volunteer"
                checked={form.workerType === 'Volunteer'}
                onChange={(v) => set('workerType', v)}
              >
                A volunteer
              </Radio>
              <Radio
                name="worker_type"
                value="Vocational/Educ Training"
                checked={form.workerType === 'Vocational/Educ Training'}
                onChange={(v) => set('workerType', v)}
              >
                Undertaking vocational or educational training
              </Radio>
            </div>
          </ChoiceGroup>

          {/* 1.7 — a Children's Worker under the Children's Act 2014 s.23(1) — is
              NOT asked: for CCAANZ the answer is always No (ADR-067, amendment
              2026-10-28), and buildBody sends false. The form's own rule then
              skips to 1.9, so 1.8 (core / non-core) is not asked either. The
              official numbers stay, and this line says why they jump from 1.6
              to 1.9 (Miranda r1 M-14). */}
          <p style={{ fontSize: 13, color: 'var(--text2)', margin: '0 0 22px' }}>
            1.7 and 1.8 — not asked: always No for CCAANZ roles.
          </p>

          <ChoiceGroup
            n="1.9"
            question="Has the vetting subject previously been Police vetted by CCAANZ?"
            required
            error={errors.previouslyVetted}
            errorId="previously-vetted-error"
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
              <Radio
                name="previously_vetted"
                value="yes"
                checked={form.previouslyVetted === 'yes'}
                onChange={(v) => set('previouslyVetted', v)}
              >
                Yes
              </Radio>
              <Radio
                name="previously_vetted"
                value="no"
                checked={form.previouslyVetted === 'no'}
                onChange={(v) => set('previouslyVetted', v)}
              >
                No (skip to 1.11)
              </Radio>
            </div>
          </ChoiceGroup>

          {/* Opened by 1.9 "Yes". Not required (validate() does not ask for it), so its
              legend carries no hidden "required", and it has no error to describe. */}
          {form.previouslyVetted === 'yes' && (
            <ChoiceGroup
              n="1.10"
              question="Is the vetting subject still working in the role for which they were last vetted?"
              hint={{
                id: 'same-role-hint',
                text: 'Select Yes if this is a renewal for the same role. Otherwise select No.',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
                <Radio
                  name="same_role"
                  value="yes"
                  checked={form.sameRoleAsPrevious === 'yes'}
                  onChange={(v) => set('sameRoleAsPrevious', v)}
                >
                  Yes
                </Radio>
                <Radio
                  name="same_role"
                  value="no"
                  checked={form.sameRoleAsPrevious === 'no'}
                  onChange={(v) => set('sameRoleAsPrevious', v)}
                >
                  No — applying for a new role or position
                </Radio>
              </div>
            </ChoiceGroup>
          )}

          <QRow>
            <QNum n="1.11" />
            <QLabel htmlFor="job-title">
              Job title of the vetting subject
              <Req />
            </QLabel>
            {/* Tied to the box too, first, as 1.3's is (Miranda r6 P1): what to write, then
                how long. */}
            <Hint id="job-title-hint">The title of the job they are being vetted for.</Hint>
            <Hint id="job-title-limit">
              Up to {JOB_TITLE_MAX_LENGTH} characters — the Police system takes no more.
            </Hint>
            <input
              id="job-title"
              type="text"
              aria-required="true"
              value={form.jobTitle}
              onChange={(e) => set('jobTitle', e.target.value)}
              style={inputStyle}
              disabled={busy}
              placeholder="e.g. Sunday School Teacher, Youth Group Leader, Vicar"
              aria-invalid={errors.jobTitle ? true : undefined}
              aria-describedby={[
                'job-title-hint',
                'job-title-limit',
                'job-title-count',
                ...(errors.jobTitle ? ['job-title-error'] : []),
              ].join(' ')}
            />
            <CharCount
              id="job-title-count"
              q="1.11"
              length={form.jobTitle.length}
              max={JOB_TITLE_MAX_LENGTH}
            />
            {errors.jobTitle && (
              <p
                id="job-title-error"
                style={{ color: 'var(--red)', fontSize: 12, margin: '4px 0 0' }}
              >
                {errors.jobTitle}
              </p>
            )}
          </QRow>

          <QRow>
            <QNum n="1.12" />
            <QLabel>Evidence of identity</QLabel>
            <div
              style={{
                background: '#fff8e6',
                border: '1px solid #f0c040',
                borderRadius: 4,
                padding: '14px 16px',
                fontSize: 14,
              }}
            >
              {isPaper
                ? 'The agency representative completed 1.12 on the paper form — record their name and the signing date in the attestation block at the end.'
                : 'Identity verification is completed by your parish representative in person. You do not need to upload documents here.'}
            </div>
          </QRow>
        </SectionCard>

        {/* ── SECTION 2 ── */}
        <SectionCard pill="SECTION 2" title="Personal Information">
          <QRow>
            <QNum n="2.1" />
            <QLabel>Information about your consent</QLabel>
            <div
              style={{
                background: '#f0f4f0',
                borderLeft: '3px solid #1e4d3f',
                padding: '14px 16px',
                borderRadius: '0 4px 4px 0',
                fontSize: 14,
                lineHeight: 1.55,
              }}
            >
              <p style={{ margin: '0 0 8px' }}>
                The purpose of this form is to allow the New Zealand Police Vetting Service to
                confirm who you are using Police records, carry out a Police vet, and obtain your
                consent to share any relevant information with the church that requested the vet.
              </p>
              <p style={{ margin: '0 0 8px' }}>
                <strong>What you are consenting to.</strong> You are agreeing that the Police
                Vetting Service can share relevant information they hold about you (if any) with the
                requesting church. This information will be used to help the church decide whether
                you are suitable for the role they are considering you for or that you are engaged
                in.
              </p>
              <p style={{ margin: '0 0 8px' }}>
                The information that may be shared is outlined in section 2.2 of this form. For
                further details, you should refer to the Vetting Bill or visit the Police vetting
                website.
              </p>
              <p style={{ margin: '0 0 8px' }}>
                You are also agreeing that the organisation that employs or engages you may provide
                information about your employment/engagement status to Police Vetting Service. See
                section 2.2(2) of this form.
              </p>
              <p style={{ margin: '0 0 8px' }}>
                <strong>Who collects your consent.</strong> The church requesting the Police vet
                will collect your signed consent.
              </p>
              <p style={{ margin: '0 0 8px' }}>
                <strong>Your rights under the Privacy Act 2020 include:</strong> you may request a
                copy of the Police vet report provided to the church by contacting the church. You
                can withdraw your consent to a Police vet at any time before the vetting process is
                complete by contacting the church. The church may keep the Police vet only for as
                long as required for vetting purposes.
              </p>
              <p style={{ margin: 0 }}>
                <strong>How long the information is valid for.</strong> The information in the
                Police vet is correct only at the time it is shared. It is up to the church to
                decide how often they ask for updated vetting.
              </p>
            </div>
          </QRow>

          <QRow>
            <QNum n="2.2" />
            <QLabel>Information about you that may be shared</QLabel>
            <div
              style={{
                background: '#f0f4f0',
                borderLeft: '3px solid #1e4d3f',
                padding: '14px 16px',
                borderRadius: '0 4px 4px 0',
                fontSize: 14,
                lineHeight: 1.55,
              }}
            >
              <p style={{ margin: '0 0 8px' }}>
                <strong>1.</strong> A Police vet will include the following information, if held by
                New Zealand Police. Please refer to the Police vetting website for more information
                regarding the Clean Slate Scheme, what may be released, and the vetting process.
              </p>
              <p style={{ margin: '0 0 4px' }}>Either:</p>
              <ul style={{ paddingLeft: 20, margin: '0 0 8px' }}>
                <li>
                  A result indicating that no information is held or relevant to the purpose of the
                  vetting request,
                </li>
              </ul>
              <p style={{ margin: '0 0 4px' }}>Or, one or both of:</p>
              <ul style={{ paddingLeft: 20, margin: '0 0 8px' }}>
                <li>
                  The vetting subject's criminal record (if any), unless the Clean Slate Scheme
                  applies.
                </li>
                <li>
                  A summary of other information that is readily retrievable, relevant to the
                  purpose of the vetting request, and deemed accurate.
                </li>
              </ul>
              <p style={{ margin: '0 0 8px' }}>
                This may include pending charges, charges without conviction, youth court charges,
                infringement offences, demerit points, arrest warrants, involvement in family
                violence, overseas convictions, police investigations without charges, interactions
                with Police, and other information held by Police.
              </p>
              <p style={{ margin: '0 0 8px' }}>
                Information may also be released where it is subject to a suppression order or
                statutory prohibition, involves offences by individuals under 18, or relates to
                mental health or substance abuse issues.
              </p>
              <p style={{ margin: '0 0 8px' }}>
                <strong>2.</strong> If you had been vetted as a Children's Worker, and you continue
                to hold that role, the Police Vetting Service may release any newly obtained
                relevant information to the requesting church at any time.
              </p>
              <p style={{ margin: '0 0 4px' }}>
                The Police Vetting Service will take reasonable steps to:
              </p>
              <ul style={{ paddingLeft: 20, margin: '0 0 8px' }}>
                <li>
                  confirm that you are still employed or engaged as a Children's Worker – For this
                  purpose, you consent to the requesting church providing the Police Vetting Service
                  with confirmation of your employment or engagement status in this role.
                </li>
                <li>notify you before any information is disclosed.</li>
              </ul>
              <p style={{ margin: 0 }}>
                <strong>3.</strong> Information provided in this consent form may be used to update
                New Zealand Police records.
              </p>
            </div>
          </QRow>

          <QRow>
            <QNum n="2.3" />
            <QLabel>Personal information</QLabel>
            <p style={{ fontSize: 13, color: '#666', margin: '0 0 14px' }}>
              Fields marked <span style={{ color: 'var(--red)' }}>*</span> are required.
            </p>

            {/* Name row — 3 columns */}
            <div className="grid-3" style={{ marginBottom: 12 }}>
              <div>
                <FieldLabel htmlFor="family-name">
                  Surname
                  <Req />
                </FieldLabel>
                <input
                  id="family-name"
                  {...fieldProps('family-name', applicantAutofill)}
                  aria-required="true"
                  value={form.familyName}
                  onChange={(e) => syncEsig({ familyName: e.target.value })}
                  style={inputStyle}
                  disabled={busy}
                />
                {errors.familyName && (
                  <p style={{ color: 'var(--red)', fontSize: 12, margin: '4px 0 0' }}>
                    {errors.familyName}
                  </p>
                )}
              </div>
              <div>
                <FieldLabel htmlFor="first-name">
                  First name
                  <Req />
                </FieldLabel>
                <input
                  id="first-name"
                  {...fieldProps('given-name', applicantAutofill)}
                  aria-required="true"
                  value={form.firstName}
                  onChange={(e) => syncEsig({ firstName: e.target.value })}
                  style={inputStyle}
                  disabled={busy}
                />
                {errors.firstName && (
                  <p style={{ color: 'var(--red)', fontSize: 12, margin: '4px 0 0' }}>
                    {errors.firstName}
                  </p>
                )}
              </div>
              <div>
                <FieldLabel htmlFor="middle-names">Middle name(s)</FieldLabel>
                <input
                  id="middle-names"
                  {...fieldProps('additional-name', applicantAutofill)}
                  value={form.middleNames}
                  onChange={(e) => syncEsig({ middleNames: e.target.value })}
                  style={inputStyle}
                  disabled={busy}
                />
              </div>
            </div>

            {/* Gender + DOB */}
            <div className="grid-2" style={{ marginBottom: 12 }}>
              <div>
                <FieldLabel htmlFor="gender">
                  Gender
                  <Req />
                </FieldLabel>
                <select
                  id="gender"
                  aria-required="true"
                  value={form.gender}
                  onChange={(e) => set('gender', e.target.value)}
                  style={inputStyle}
                  disabled={busy}
                >
                  <option value="">Select…</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
                {errors.gender && (
                  <p style={{ color: 'var(--red)', fontSize: 12, margin: '4px 0 0' }}>
                    {errors.gender}
                  </p>
                )}
              </div>
              <div>
                <FieldLabel htmlFor="date-of-birth">
                  Date of birth
                  <Req />{' '}
                  <span style={{ fontWeight: 400, color: 'var(--text2)', fontSize: 12 }}>
                    {isPaper ? '(14+ on the date signed)' : '(must be 14+)'}
                  </span>
                </FieldLabel>
                <input
                  id="date-of-birth"
                  type="date"
                  aria-required="true"
                  value={form.dateOfBirth}
                  onChange={(e) => set('dateOfBirth', e.target.value)}
                  style={inputStyle}
                  disabled={busy}
                  max={stamp()}
                />
                {errors.dateOfBirth && (
                  <p style={{ color: 'var(--red)', fontSize: 12, margin: '4px 0 0' }}>
                    {errors.dateOfBirth}
                  </p>
                )}
              </div>
            </div>

            {/* Place + Country */}
            <div className="grid-2" style={{ marginBottom: 12 }}>
              <div>
                <FieldLabel htmlFor="place-of-birth">
                  Place of birth (Town/City/State)
                  <Req />
                </FieldLabel>
                <input
                  id="place-of-birth"
                  type="text"
                  aria-required="true"
                  value={form.placeOfBirth}
                  onChange={(e) => set('placeOfBirth', e.target.value)}
                  style={inputStyle}
                  disabled={busy}
                />
                {errors.placeOfBirth && (
                  <p style={{ color: 'var(--red)', fontSize: 12, margin: '4px 0 0' }}>
                    {errors.placeOfBirth}
                  </p>
                )}
              </div>
              <div>
                <FieldLabel htmlFor="country-of-birth">
                  Country of birth
                  <Req />
                </FieldLabel>
                <select
                  id="country-of-birth"
                  aria-required="true"
                  value={form.countryOfBirth}
                  onChange={(e) => set('countryOfBirth', e.target.value)}
                  style={{ ...inputStyle, cursor: 'pointer' }}
                  disabled={busy}
                >
                  <option value="">Select country…</option>
                  {COUNTRIES.map((c) =>
                    c.startsWith('─') ? (
                      <option key={c} disabled>
                        {c}
                      </option>
                    ) : (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ),
                  )}
                </select>
                {errors.countryOfBirth && (
                  <p style={{ color: 'var(--red)', fontSize: 12, margin: '4px 0 0' }}>
                    {errors.countryOfBirth}
                  </p>
                )}
              </div>
            </div>

            {/* Licence */}
            <div style={{ marginBottom: 12, maxWidth: 220 }}>
              <FieldLabel htmlFor="nz-driver-licence">NZ Driver Licence number</FieldLabel>
              {/* A government id — autofill and form history off on EVERY route: on a shared
                  device the browser would otherwise keep one person's licence number, keyed by
                  this fixed id, and offer it to the next (Lazo L2; cca-piv70). */}
              <input
                id="nz-driver-licence"
                {...fieldProps('government-id')}
                value={form.nzDriverLicence}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const v = e.target.value.toUpperCase();
                  set('nzDriverLicence', v);
                  setLicenceError(false);
                }}
                onBlur={() => {
                  const v = form.nzDriverLicence.trim();
                  setLicenceError(v !== '' && !isValidLicence(v));
                }}
                maxLength={8}
                style={inputStyle}
                disabled={busy}
                placeholder="e.g. AB123456"
              />
              {licenceError && (
                <p style={{ color: 'var(--red)', fontSize: 12, margin: '4px 0 0' }}>
                  Must be 2 letters + 6 digits (e.g. AB123456).
                </p>
              )}
            </div>

            {/* Guardian */}
            {isMinor && (
              <div
                style={{
                  background: '#fff8e6',
                  border: '1px solid #f0c040',
                  borderRadius: 4,
                  padding: '14px 16px',
                  marginBottom: 12,
                }}
              >
                {/* The paper route speaks of the applicant (Miranda r2 S2) on the
                    day the age is judged on: the date signed, once a usable one
                    is entered, and today until then, the state a registrar
                    transcribing top to bottom meets first (Miranda r3). */}
                <p style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>
                  {!isPaper
                    ? 'You appear to be 14 or 15 years of age. A parent or guardian must also provide consent.'
                    : judgedOnSignedDay
                      ? 'The applicant was 14 or 15 on the date the paper form was signed, so a parent or guardian must also have consented. Enter their name as written on the paper form.'
                      : "The applicant is 14 or 15 today, so a parent or guardian's name is likely needed. The date the paper form was signed decides — enter it under Authorisation."}
                </p>
                {/* They sign for themselves under Authorisation (cca-52uxp). */}
                {!isPaper && (
                  <p style={{ fontSize: 13, margin: '0 0 8px' }}>
                    Your parent or guardian needs to be with you to sign at the end of this form.
                  </p>
                )}
                {/* True with no signed date, and with one that cannot be used yet. */}
                {isPaper && !judgedOnSignedDay && (
                  <p style={{ fontSize: 13, margin: '0 0 8px' }}>
                    Until a usable signed date is entered (today or earlier, and not before the date
                    of birth), this is judged on today's date.
                  </p>
                )}
                <FieldLabel htmlFor={GUARDIAN_NAME_ID}>
                  Name of parent or guardian
                  <Req />
                </FieldLabel>
                {/* Someone else's name, so autofill is off: the browser must not
                    offer the applicant's own saved name (cca-52uxp). */}
                <input
                  id={GUARDIAN_NAME_ID}
                  ref={guardianNameRef}
                  {...fieldProps('name', { proxy: true })}
                  aria-required="true"
                  value={form.guardianName}
                  onChange={(e) => set('guardianName', e.target.value)}
                  style={inputStyle}
                  disabled={busy}
                  aria-invalid={errors.guardianName ? true : undefined}
                  aria-describedby={errors.guardianName ? 'guardian-name-error' : undefined}
                />
                {errors.guardianName && (
                  <p
                    id="guardian-name-error"
                    style={{ color: 'var(--red)', fontSize: 12, margin: '4px 0 0' }}
                  >
                    {errors.guardianName}
                  </p>
                )}
              </div>
            )}
          </QRow>

          {/* 2.4 Other names */}
          <QRow>
            <QNum n="2.4" />
            <QLabel>Other names</QLabel>
            <Hint>
              All other names you are or have been known by — maiden names, aliases, name changes.
              Include surname, first and middle names.
            </Hint>
            <div className="table-wrap">
              <table style={{ width: '100%', minWidth: 360, borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {OTHER_NAME_COLUMNS.map(({ field, header }) => (
                      <th key={field} style={otherNameHeaderStyle}>
                        {header}
                      </th>
                    ))}
                    {/* The × column's header is heard, not seen: "Remove", so table navigation
                        names the column of remove buttons. `datefield-sr` is @cca/ui's shared
                        visually-hidden rule (components.css; Miranda P3, cca-piv70).
                        position: relative — that span is absolutely positioned, so without a
                        positioned ancestor INSIDE .table-wrap it escapes the scroll container
                        and widens the whole page at 320px (WCAG 1.4.10 Reflow; cca-piv70). */}
                    <th style={{ ...otherNameHeaderStyle, width: 36, position: 'relative' }}>
                      <span className="datefield-sr">Remove</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {form.otherNames.map((row, i) => (
                    <tr key={i}>
                      {OTHER_NAME_COLUMNS.map(({ field, header }) => (
                        <td key={field} style={{ padding: '4px 4px' }}>
                          <input
                            type="text"
                            className="nzpv-input-sm"
                            aria-label={`Other name ${i + 1}, ${header}`}
                            value={row[field]}
                            disabled={busy}
                            maxLength={OTHER_NAME_MAX_LENGTH}
                            // The row the 2.4 refusal is about: its three boxes
                            // are invalid and point at the sentence (Miranda r1
                            // M-3), as 2.5's boxes do (cca-vjsa).
                            aria-invalid={
                              errors.otherNames && otherNamesRow === i + 1 ? true : undefined
                            }
                            aria-describedby={
                              errors.otherNames && otherNamesRow === i + 1
                                ? 'other-names-error'
                                : undefined
                            }
                            style={{
                              width: '100%',
                              padding: '7px 10px',
                              border: '1px solid #ddd',
                              borderRadius: 3,
                              fontFamily: 'inherit',
                            }}
                            onChange={(e) => {
                              const next = form.otherNames.map((r, j) =>
                                j === i ? { ...r, [field]: e.target.value } : r,
                              );
                              setOtherNames(next);
                            }}
                          />
                        </td>
                      ))}
                      <td style={{ padding: '4px 4px', textAlign: 'center' }}>
                        <button
                          type="button"
                          aria-label={`Remove other name ${i + 1}`}
                          disabled={form.otherNames.length === 1 || busy}
                          onClick={() => setOtherNames(form.otherNames.filter((_, j) => j !== i))}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--red)',
                            cursor: 'pointer',
                            fontSize: 20,
                            lineHeight: 1,
                            padding: '0 4px',
                            opacity: form.otherNames.length === 1 ? 0.3 : 1,
                          }}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {errors.otherNames && (
              <p
                id="other-names-error"
                style={{ color: 'var(--red)', fontSize: 12, margin: '4px 0 8px' }}
              >
                {errors.otherNames}
              </p>
            )}
            {/* At the row cap the button LOOKS off as well as being off — no
                pointer, muted — and the cap's own sentence says what to do
                instead (Miranda r1 M-13). Not repeated when 2.4's refusal is
                already that sentence. */}
            {namesCapNote && (
              <p
                id="other-names-cap"
                style={{ color: 'var(--text2)', fontSize: 12, margin: '4px 0 8px' }}
              >
                {OTHER_NAMES_MESSAGES.tooMany}
              </p>
            )}
            <button
              type="button"
              disabled={addNameOff}
              aria-describedby={namesCapNote ? 'other-names-cap' : undefined}
              onClick={() =>
                setOtherNames([
                  ...form.otherNames,
                  { familyName: '', firstName: '', middleNames: '' },
                ])
              }
              style={{
                background: 'none',
                border: `1px dashed ${addNameOff ? 'var(--text2)' : '#1e4d3f'}`,
                color: addNameOff ? 'var(--text2)' : '#1e4d3f',
                padding: '6px 14px',
                borderRadius: 4,
                fontSize: 13,
                cursor: addNameOff ? 'default' : 'pointer',
              }}
            >
              + Add another name
            </button>
          </QRow>

          {/* 2.5 Address */}
          <QRow>
            <QNum n="2.5" />
            <QLabel>Permanent residential address</QLabel>
            {/* Each part carries a visible <label htmlFor> (not a placeholder, which vanishes
                on typing) and its error is linked by aria-describedby + aria-invalid while it
                shows — UX-4, WCAG 2.2 SC 1.3.1 / 3.3.2 (cca-vjsa). */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <FieldLabel htmlFor="address-street">
                  Flat/number and street name
                  <Req />
                </FieldLabel>
                <input
                  id="address-street"
                  {...fieldProps('street-address', applicantAutofill)}
                  aria-required="true"
                  value={form.addressStreet}
                  onChange={(e) => set('addressStreet', e.target.value)}
                  style={inputStyle}
                  disabled={busy}
                  aria-invalid={errors.addressStreet ? true : undefined}
                  aria-describedby={errors.addressStreet ? 'address-street-error' : undefined}
                />
                {errors.addressStreet && (
                  <p
                    id="address-street-error"
                    style={{ color: 'var(--red)', fontSize: 12, margin: '4px 0 0' }}
                  >
                    {errors.addressStreet}
                  </p>
                )}
              </div>
              <div>
                <FieldLabel htmlFor="address-suburb">Suburb</FieldLabel>
                <input
                  id="address-suburb"
                  {...fieldProps('address-level3', applicantAutofill)}
                  value={form.addressSuburb}
                  onChange={(e) => set('addressSuburb', e.target.value)}
                  style={inputStyle}
                  disabled={busy}
                />
              </div>
              <div className="grid-2">
                <div>
                  <FieldLabel htmlFor="address-postcode">
                    Postcode
                    <Req />
                  </FieldLabel>
                  <input
                    id="address-postcode"
                    {...fieldProps('postcode', applicantAutofill)}
                    aria-required="true"
                    value={form.addressPostcode}
                    onChange={(e) =>
                      set('addressPostcode', e.target.value.replace(/\D/g, '').slice(0, 4))
                    }
                    style={inputStyle}
                    disabled={busy}
                    maxLength={4}
                    aria-invalid={errors.addressPostcode ? true : undefined}
                    aria-describedby={errors.addressPostcode ? 'address-postcode-error' : undefined}
                  />
                  {errors.addressPostcode && (
                    <p
                      id="address-postcode-error"
                      style={{ color: 'var(--red)', fontSize: 12, margin: '4px 0 0' }}
                    >
                      {errors.addressPostcode}
                    </p>
                  )}
                </div>
                <div>
                  <FieldLabel htmlFor="address-towncity">
                    Town/City
                    <Req />
                  </FieldLabel>
                  <input
                    id="address-towncity"
                    {...fieldProps('address-level2', applicantAutofill)}
                    aria-required="true"
                    value={form.addressTownCity}
                    onChange={(e) => set('addressTownCity', e.target.value)}
                    style={inputStyle}
                    disabled={busy}
                    aria-invalid={errors.addressTownCity ? true : undefined}
                    aria-describedby={errors.addressTownCity ? 'address-towncity-error' : undefined}
                  />
                  {errors.addressTownCity && (
                    <p
                      id="address-towncity-error"
                      style={{ color: 'var(--red)', fontSize: 12, margin: '4px 0 0' }}
                    >
                      {errors.addressTownCity}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </QRow>

          {/* E-signature */}
          <div
            style={{
              background: '#f0f4f0',
              border: '1px solid #1e4d3f',
              borderRadius: 6,
              padding: 20,
              marginTop: 24,
            }}
          >
            <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#1e4d3f' }}>
              {isPaper
                ? 'Authorisation — signed paper original'
                : 'Authorisation of vetting subject'}
            </h3>
            {isPaper ? (
              <div style={{ fontSize: 14, marginBottom: 16, lineHeight: 1.6 }}>
                The applicant and the agency representative signed the paper form in ink. Record the
                details from the original below — your attestation replaces the portal e-signatures
                as the recorded basis of consent.
              </div>
            ) : (
              <div style={{ fontSize: 14, marginBottom: 16, lineHeight: 1.6 }}>
                By signing this form:
                <ul style={{ paddingLeft: 20, margin: '8px 0 0' }}>
                  <li>
                    I confirm that the information I have provided is about me and is correct.
                  </li>
                  <li>I have read and understood the information above.</li>
                  <li>
                    I authorise NZ Police to disclose relevant personal information to CCAANZ.
                  </li>
                </ul>
              </div>
            )}

            <div style={{ marginBottom: 14 }}>
              <div style={{ marginBottom: 10 }}>
                <FieldLabel htmlFor="esig-name">
                  {isPaper ? "Applicant's full legal name (as on the paper form)" : 'Full name'}
                  <Req />
                </FieldLabel>
                {/* On the paper route the applicant is not the one typing, so neither
                    placeholder speaks to "you" there (Miranda S1; cca-piv70). */}
                <input
                  id="esig-name"
                  {...fieldProps('name', applicantAutofill)}
                  aria-required="true"
                  value={form.esigName}
                  onChange={(e) => {
                    set('esigName', e.target.value);
                    set('esigNameManuallyEdited', true);
                  }}
                  style={inputStyle}
                  disabled={busy}
                  placeholder={isPaper ? undefined : 'Your full legal name'}
                />
                {errors.esigName && (
                  <p style={{ color: 'var(--red)', fontSize: 12, margin: '4px 0 0' }}>
                    {errors.esigName}
                  </p>
                )}
              </div>
              <div style={{ marginBottom: 10 }}>
                <FieldLabel htmlFor="esig-email">
                  {isPaper ? "Applicant's email address" : 'Email address'}
                  <Req />
                </FieldLabel>
                <input
                  id="esig-email"
                  {...fieldProps('email', applicantAutofill)}
                  aria-required="true"
                  value={form.esigEmail}
                  onChange={(e) => set('esigEmail', e.target.value)}
                  style={inputStyle}
                  disabled={busy}
                  placeholder={isPaper ? undefined : 'your@email.com'}
                  aria-invalid={errors.esigEmail ? true : undefined}
                  aria-describedby={errors.esigEmail ? 'esig-email-error' : undefined}
                />
                {errors.esigEmail && (
                  <p
                    id="esig-email-error"
                    style={{ color: 'var(--red)', fontSize: 12, margin: '4px 0 0' }}
                  >
                    {errors.esigEmail}
                  </p>
                )}
              </div>
              {isPaper ? (
                <>
                  <div style={{ marginBottom: 10, maxWidth: 220 }}>
                    <FieldLabel htmlFor="paper-signed-date">
                      Date the paper form was signed
                      <Req />{' '}
                      <span style={{ fontWeight: 400, color: 'var(--text2)', fontSize: 12 }}>
                        (today or earlier)
                      </span>
                    </FieldLabel>
                    <input
                      id="paper-signed-date"
                      type="date"
                      aria-required="true"
                      value={form.paperSignedDate}
                      onChange={(e) => set('paperSignedDate', e.target.value)}
                      style={inputStyle}
                      disabled={busy}
                      max={stamp()}
                    />
                    {errors.paperSignedDate && (
                      <p style={{ color: 'var(--red)', fontSize: 12, margin: '4px 0 0' }}>
                        {errors.paperSignedDate}
                      </p>
                    )}
                  </div>
                  {/* Says when this date opens the guardian box at 2.3, or puts an
                      applicant who is 14 or more today under 14 (Miranda r2 S1,
                      r3), far up the page from here. Each sentence stands on its
                      own, as a screen reader reads it alone. On the page from the
                      first render and empty, so a screen reader announces only a
                      change; role="status" is polite and reads the whole line
                      (never role="alert"). Outside the 220px box above, so the
                      sentence is not wrapped a word or two to a line. */}
                  <div
                    role="status"
                    style={{
                      fontSize: 13,
                      lineHeight: 1.5,
                      margin: signedDayChange ? '0 0 10px' : 0,
                    }}
                  >
                    {signedDayChange === 'opens' && (
                      <>
                        On the date the paper form was signed, the applicant was {age}, so a parent
                        or guardian's name is needed —{' '}
                        <a
                          href={`#${GUARDIAN_NAME_ID}`}
                          className="btn-link"
                          onClick={(ev) => {
                            ev.preventDefault();
                            guardianNameRef.current?.focus();
                          }}
                        >
                          enter it at 2.3
                        </a>
                        .
                      </>
                    )}
                    {signedDayChange === 'under14' && PAPER_SIGNED_UNDER_14_NOTE}
                  </div>
                  <div style={{ marginBottom: 10 }}>
                    <FieldLabel htmlFor="paper-approver-name">
                      Agency representative who signed section 1.12 (full name)
                      <Req />
                    </FieldLabel>
                    <input
                      id="paper-approver-name"
                      type="text"
                      aria-required="true"
                      value={form.paperApproverName}
                      onChange={(e) => set('paperApproverName', e.target.value)}
                      style={inputStyle}
                      disabled={busy}
                      placeholder="As signed on the paper form"
                    />
                    {errors.paperApproverName && (
                      <p style={{ color: 'var(--red)', fontSize: 12, margin: '4px 0 0' }}>
                        {errors.paperApproverName}
                      </p>
                    )}
                  </div>
                  <div>
                    <FieldLabel htmlFor="transcriber-name">
                      Your full name (transcriber)
                      <Req />
                    </FieldLabel>
                    {/* The one name here that IS the person at the keyboard: their own
                        `name` autofill (cca-piv70). */}
                    <input
                      id="transcriber-name"
                      {...fieldProps('name')}
                      aria-required="true"
                      value={form.transcriberName}
                      onChange={(e) => set('transcriberName', e.target.value)}
                      style={inputStyle}
                      disabled={busy}
                      placeholder="Your full legal name"
                    />
                    {errors.transcriberName && (
                      <p style={{ color: 'var(--red)', fontSize: 12, margin: '4px 0 0' }}>
                        {errors.transcriberName}
                      </p>
                    )}
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <FieldLabel htmlFor="wet-signature-file-link">
                      Wet-signature consent file (optional)
                    </FieldLabel>
                    <input
                      id="wet-signature-file-link"
                      type="url"
                      value={form.wetSignatureFileLink}
                      onChange={(e) => set('wetSignatureFileLink', e.target.value)}
                      style={inputStyle}
                      disabled={busy}
                      placeholder="Paste the Drive link to the signed consent PDF"
                    />
                    <p style={{ color: '#666', fontSize: 12, margin: '4px 0 0' }}>
                      Paste the Drive link to the signed form from the NZPV Applications archive.
                      We&rsquo;ll copy it into the applicant&rsquo;s folder and link it on the
                      lodgement worksheet.
                    </p>
                  </div>
                </>
              ) : (
                <div>
                  <FieldLabel htmlFor="esig-date">Date</FieldLabel>
                  <input
                    id="esig-date"
                    type="text"
                    value={todayLabel()}
                    readOnly
                    style={{ ...inputStyle, background: '#f7f7f7', color: '#555', maxWidth: 220 }}
                  />
                </div>
              )}
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: 14,
                background: 'white',
                borderRadius: 4,
                border: '1px solid #c8ddd8',
              }}
            >
              <input
                type="checkbox"
                id="esig-consent"
                aria-required="true"
                checked={form.esigAccepted}
                onChange={(e) => set('esigAccepted', e.target.checked)}
                style={{
                  width: 20,
                  height: 20,
                  marginTop: 1,
                  flexShrink: 0,
                  accentColor: '#1e4d3f',
                  cursor: 'pointer',
                }}
                disabled={busy}
              />
              <label htmlFor="esig-consent" style={{ fontSize: 14, cursor: 'pointer' }}>
                {isPaper ? (
                  <>
                    I hold the original signed paper form and confirm I have transcribed it
                    faithfully. The original — bearing the handwritten signatures of the applicant
                    and the agency representative — will be retained by the Registrar and lodged
                    with the NZ Police Vetting Service. Ticking this box constitutes my electronic
                    signature on this attestation.
                  </>
                ) : (
                  <>
                    Ticking this box constitutes my electronic signature and my consent for its use
                    in place of a handwritten signature on this form. I understand this has the same
                    legal effect as a handwritten signature.
                  </>
                )}
                <Req />
              </label>
            </div>
            {errors.esigAccepted && (
              <p style={{ color: 'var(--red)', fontSize: 12, margin: '6px 0 0' }}>
                {errors.esigAccepted}
              </p>
            )}
          </div>

          {/* Guardian e-sig — paper minors' guardian signed the original in ink */}
          {isMinor && !isPaper && (
            <div
              style={{
                background: '#fffbf0',
                border: '1px solid #e0a030',
                borderRadius: 6,
                padding: 20,
                marginTop: 16,
              }}
            >
              <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#a06000' }}>
                Parent/Guardian authorisation
              </h3>
              {/* The guardian's own signature (cca-52uxp): they type their name and
                  tick the box themselves. The route records the e-signature under
                  this name, never 2.3's. Each refusal is linked to its control by
                  aria-describedby + aria-invalid while it shows, as 2.5's are. */}
              <p style={{ fontWeight: 600, fontSize: 14, margin: '0 0 6px' }}>
                Now hand this device to your parent or guardian.
              </p>
              <p style={{ fontSize: 14, margin: '0 0 12px', lineHeight: 1.6 }}>
                Parent or guardian: type your own full name and tick the box yourself. This is your
                electronic signature.
              </p>
              <div style={{ marginBottom: 14 }}>
                <FieldLabel htmlFor="guardian-esig-name">
                  Your full legal name (parent or guardian)
                  <Req />
                </FieldLabel>
                {/* Autofill off: the browser must not offer the applicant's own
                    saved name as the guardian's signature. */}
                <input
                  id="guardian-esig-name"
                  {...fieldProps('name', { proxy: true })}
                  aria-required="true"
                  value={form.guardianEsigName}
                  onChange={(e) => set('guardianEsigName', e.target.value)}
                  style={inputStyle}
                  disabled={busy}
                  placeholder="Parent or guardian's full legal name"
                  aria-invalid={errors.guardianEsigName ? true : undefined}
                  aria-describedby={
                    errors.guardianEsigName ? 'guardian-esig-name-error' : undefined
                  }
                />
                {errors.guardianEsigName && (
                  <p
                    id="guardian-esig-name-error"
                    style={{ color: 'var(--red)', fontSize: 12, margin: '4px 0 0' }}
                  >
                    {errors.guardianEsigName}
                  </p>
                )}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  padding: 14,
                  background: 'white',
                  borderRadius: 4,
                  border: '1px solid #f0d080',
                }}
              >
                <input
                  type="checkbox"
                  id="guardian-esig"
                  aria-required="true"
                  checked={form.guardianEsigAccepted}
                  onChange={(e) => set('guardianEsigAccepted', e.target.checked)}
                  aria-invalid={errors.guardianEsigAccepted ? true : undefined}
                  aria-describedby={errors.guardianEsigAccepted ? 'guardian-esig-error' : undefined}
                  style={{
                    width: 20,
                    height: 20,
                    marginTop: 1,
                    flexShrink: 0,
                    // The heading's amber, 5.04:1 on white (WCAG 1.4.11 asks 3:1;
                    // the #e0a030 it replaces was 2.3:1).
                    accentColor: '#a06000',
                    cursor: 'pointer',
                  }}
                  disabled={busy}
                />
                {/* The applicant's signature sentence, in the guardian's own voice
                    (cca-52uxp). The route records these words as the guardian's
                    signature text (consent-form.ts captureIntake). */}
                <label htmlFor="guardian-esig" style={{ fontSize: 14, cursor: 'pointer' }}>
                  As parent or guardian of the applicant, I consent to this Police vetting
                  application. Ticking this box constitutes my electronic signature and my consent
                  for its use in place of a handwritten signature on this form. I understand this
                  has the same legal effect as a handwritten signature.
                  <Req />
                </label>
              </div>
              {errors.guardianEsigAccepted && (
                <p
                  id="guardian-esig-error"
                  style={{ color: 'var(--red)', fontSize: 12, margin: '6px 0 0' }}
                >
                  {errors.guardianEsigAccepted}
                </p>
              )}
            </div>
          )}
        </SectionCard>

        {/* What happens after Submit, said once and directly above the bar, with
            nothing in between — and named as Submit's description, so it is read
            out with the button. It used to sit inside the bar, under Submit, where
            on a phone it was pinned over the form as a second row of text and
            pushed the docked "Report a problem" launcher onto a third (Tyson:
            the bar 190px of a 256px screen at 400% zoom; 128.5px without it,
            cca-00fff). It stays on the page while the form is submitting, so the
            description never names a missing element. */}
        <p
          id="submit-note"
          style={{ fontSize: 13, color: 'var(--text2)', marginTop: 12, textAlign: 'center' }}
        >
          {isPaper
            ? 'The application is approved on the basis of the paper original and goes straight to your lodgement queue.'
            : 'Your application will be reviewed by your parish before being lodged with NZ Police Vetting.'}
        </p>

        {/* Submit. Through StickyActionBar, not a bare `.action-bar` (cca-iex84): the
            same row, but while it is pinned on a phone it publishes its height and
            docks the "Report a problem" launcher as its trailing control, and Tab
            stops a focused field above it rather than under it (components.css,
            cca-pftjr; SC 2.4.11). */}
        <StickyActionBar style={{ justifyContent: 'center', marginTop: 8, marginBottom: 40 }}>
          <button
            type="submit"
            disabled={busy}
            aria-describedby="submit-note"
            style={{
              background: '#1e4d3f',
              color: 'white',
              border: 'none',
              padding: '14px 48px',
              fontSize: 16,
              fontWeight: 600,
              borderRadius: 6,
              cursor: busy ? 'not-allowed' : 'pointer',
              opacity: busy ? 0.7 : 1,
            }}
          >
            {busy ? (
              <>
                <span className="busy-spinner" />
                Submitting…
              </>
            ) : isPaper ? (
              'Enter paper application'
            ) : (
              'Submit Application'
            )}
          </button>
          {/* While it submits, the bar itself says so: pinned, it is in sight. */}
          {busy && (
            <p style={{ fontSize: 13, color: '#1e4d3f', fontWeight: 600, marginTop: 12 }}>
              Submitting your application — this can take a few seconds. Please don't close this
              page.
            </p>
          )}
        </StickyActionBar>

        <p style={{ fontSize: 12, color: 'var(--text2)', textAlign: 'center', marginBottom: 32 }}>
          Personal information held under the Policing (Police Vetting) Amendment Bill and the
          Privacy Act 2020. Questions?{' '}
          <a href="mailto:nzpv@confessinganglicans.nz" className="btn-link">
            nzpv@confessinganglicans.nz
          </a>
        </p>
      </form>
    </div>
  );
}
